import { useState, useEffect } from 'react';
import { FaCheck, FaTimes, FaRegStar, FaSort, FaCalendarAlt } from 'react-icons/fa';
import { GoSearch } from 'react-icons/go';
import api from '../../services/Api';

export default function TestimonialReviewPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [selectedTestimonial, setSelectedTestimonial] = useState(null);
  const [dialogAction, setDialogAction] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await api.get('/testimony-placements');
        setTestimonials(response.data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  useEffect(() => {
    const handleClickOutside = event => {
      const sortDropdown = document.querySelector('.sort-dropdown');
      const sortButton = document.querySelector('.sort-button');

      if (
        showSortDropdown &&
        sortDropdown &&
        sortButton &&
        !sortDropdown.contains(event.target) &&
        !sortButton.contains(event.target)
      ) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showSortDropdown]);

  const handleOpenDialog = async (testimonial, action) => {
    try {
      const response = await api.get(`/testimony-placements/${testimonial._id}`);
      setSelectedTestimonial(response.data);
      setDialogAction(action);
    } catch (error) {
      console.error('Error fetching testimonial details:', error);
      alert('Failed to load testimonial details. Please try again.');
    }
  };

  const handleConfirmAction = async () => {
    if (!selectedTestimonial) return;

    try {
      const updatedTestimonial = {
        ...selectedTestimonial,
        show: dialogAction === 'accept' ? true : false,
      };

      await api.patch(`/testimony-placements/${selectedTestimonial._id}`, updatedTestimonial);

      if (dialogAction === 'accept') {
        console.log(`Accepted testimonial: ${selectedTestimonial.title}`);
      } else {
        console.log(`Denied testimonial: ${selectedTestimonial.title}`);
      }

      setTestimonials(prev => prev.filter(t => t._id !== selectedTestimonial._id));
      setSelectedTestimonial(null);
      setDialogAction(null);
    } catch (error) {
      console.error(
        `Error ${dialogAction === 'accept' ? 'accepting' : 'denying'} testimonial:`,
        error
      );
      alert(`Failed to ${dialogAction} testimonial. Please try again.`);
    }
  };

  const handleCloseDialog = () => {
    setSelectedTestimonial(null);
    setDialogAction(null);
  };

  const handleSearch = e => {
    setSearchTerm(e.target.value);
  };

  const toggleSortDropdown = () => {
    setShowSortDropdown(!showSortDropdown);
  };

  const handleSort = sortType => {
    setSortBy(sortType);
    setShowSortDropdown(false);
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredTestimonials = testimonials
    .filter(
      testimonial =>
        testimonial.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        testimonial.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'newest') {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
    });

  if (isLoading) {
    return (
      <div className="max-w-6xl p-6 mx-auto">
        <div className="animate-pulse">
          <div className="w-1/4 h-8 mb-8 bg-gray-200 rounded"></div>
          <div className="flex mb-6 space-x-4">
            <div className="w-1/3 h-10 bg-gray-200 rounded"></div>
            <div className="w-1/4 h-10 bg-gray-200 rounded"></div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="p-6 border rounded-lg bg-gray-50">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 mr-3 bg-gray-200 rounded-full"></div>
                  <div>
                    <div className="w-24 h-4 mb-2 bg-gray-200 rounded"></div>
                    <div className="w-32 h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
                <div className="mb-4 space-y-2">
                  <div className="w-full h-3 bg-gray-200 rounded"></div>
                  <div className="w-full h-3 bg-gray-200 rounded"></div>
                  <div className="w-3/4 h-3 bg-gray-200 rounded"></div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="w-24 h-4 bg-gray-200 rounded"></div>
                  <div className="flex space-x-2">
                    <div className="w-20 h-8 bg-gray-200 rounded"></div>
                    <div className="w-20 h-8 bg-gray-200 rounded"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-base font-semibold text-gray-800">Testimonials Review</h1>
        <div className="text-sm font-bold text-gray-500">
          {filteredTestimonials.length} pending reviews
        </div>
      </div>

      {/* Search and sort */}
      <div className="flex flex-col mb-6 space-y-4 md:flex-row md:space-y-0 md:space-x-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 mb-1.5 flex items-center pointer-events-none">
            <GoSearch className="text-gray-800" />
          </div>
          <input
            type="text"
            placeholder="Search testimonials..."
            className="block w-full py-2 pl-10 pr-4 text-sm border rounded-lg focus:outline-none focus:ring-0"
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="relative">
          <button
            className="flex items-center px-4 py-2 text-sm bg-white border border-gray-300 rounded-lg sort-button"
            onClick={toggleSortDropdown}>
            <FaSort className="mr-2 text-gray-500" />
            <span>Sort</span>
          </button>
          {showSortDropdown && (
            <div className="absolute right-0 z-10 w-48 mt-2 bg-white rounded-md shadow-lg sort-dropdown">
              <div className="py-1">
                <button
                  className={`block px-4 py-2 text-sm w-full text-left ${
                    sortBy === 'newest' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleSort('newest')}>
                  Newest First
                </button>
                <button
                  className={`block px-4 py-2 text-sm w-full text-left ${
                    sortBy === 'oldest' ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                  onClick={() => handleSort('oldest')}>
                  Oldest First
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {filteredTestimonials.length === 0 ? (
        <div className="py-12 text-center rounded-lg bg-gray-50">
          <div className="mb-4 text-5xl text-gray-400">
            <FaRegStar className="mx-auto" />
          </div>
          <h3 className="mb-1 text-lg font-medium text-gray-700">No testimonials to review</h3>
          <p className="text-gray-500">All caught up! Check back later for new submissions.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          {filteredTestimonials.map(testimonial => (
            <div
              key={testimonial._id}
              className="overflow-hidden transition bg-white border rounded-lg">
              {/* Card Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b">
                <div className="flex items-center">
                  <div className="flex items-center justify-center w-10 h-10 mr-3 rounded-full">
                    <img
                      src={testimonial?.userID?.profilePictureUrl}
                      className="w-full h-full rounded-full "
                      alt=""
                    />
                  </div>
                  <div>
                    <h2 className="text-sm font-medium text-gray-800">
                      {testimonial?.userID.firstName} {testimonial?.userID?.lastName}
                    </h2>
                    <p className="text-sm text-gray-500">{testimonial?.userID?.title}</p>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="px-6 py-4">
                <p className="mb-4 text-sm text-black">{testimonial?.title}</p>
                <p className="mb-4 text-sm text-gray-700">{testimonial?.description}</p>
                <div className="flex items-center mb-4 text-sm text-gray-500">
                  <FaCalendarAlt className="mr-1" />
                  <span className="text-sm">Submitted on {formatDate(testimonial?.createdAt)}</span>
                </div>
                <div className="flex space-x-2">
                  {!testimonial.show && (
                    <>
                      <button
                        className="flex items-center px-4 py-2 text-sm font-medium text-green-600 transition border border-green-200 rounded-md bg-green-50 hover:bg-green-100"
                        onClick={() => handleOpenDialog(testimonial, 'accept')}>
                        <FaCheck className="mr-2" /> Accept
                      </button>
                      <button
                        className="flex items-center px-4 py-2 text-sm font-medium text-red-600 transition border border-red-200 rounded-md bg-red-50 hover:bg-red-100"
                        onClick={() => handleOpenDialog(testimonial, 'deny')}>
                        <FaTimes className="mr-2" /> Deny
                      </button>
                    </>
                  )}
                  {testimonial.show && (
                    <div className="flex items-center text-sm text-green-600">
                      <FaCheck className="mr-1" /> Currently Published
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirmation Dialog */}
      {selectedTestimonial && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 animate-fadeIn">
          <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl animate-scaleIn">
            <h2 className="mb-1 text-xl font-bold">
              {dialogAction === 'accept' ? 'Accept Testimonial' : 'Deny Testimonial'}
            </h2>
            <p className="mb-4 text-sm text-gray-500">This action cannot be undone</p>

            {/* Testimonial preview */}
            <div className="p-4 mb-4 border border-gray-200 rounded-md bg-gray-50">
              <div className="flex items-center mb-2">
                <div className="flex items-center justify-center w-8 h-8 mr-2 bg-gray-300 rounded-full">
                  <img
                    src={selectedTestimonial?.userID?.profilePictureUrl}
                    className="w-full h-full rounded-full "
                    alt=""
                  />
                </div>
                <div>
                  <p className="font-medium text-gray-800">
                    {selectedTestimonial?.userID?.firstName} {selectedTestimonial?.userID?.lastName}
                  </p>
                  <p className="text-sm text-gray-500">{selectedTestimonial?.userID?.title}</p>
                </div>
              </div>
              <p className="font-medium text-gray-800">{selectedTestimonial?.title}</p>
              <p className="text-sm text-gray-600">{selectedTestimonial?.description}</p>
              <p className="mt-2 text-sm text-gray-500">
                Status: {selectedTestimonial.show ? 'Currently Shown' : 'Currently Hidden'}
              </p>
            </div>

            <p className="mb-6">
              Are you sure you want to {dialogAction === 'accept' ? 'publish' : 'remove'} this
              testimonial?
            </p>

            <div className="flex justify-end space-x-2">
              <button
                className="px-4 py-2 font-medium transition border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={handleCloseDialog}>
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-md text-white transition font-medium ${
                  dialogAction === 'accept'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
                onClick={handleConfirmAction}>
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
